import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useAnimation, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import HamburgerIcon from "./HamburgerIcon";

const Header = ({ headerToggle, setHeaderToggle }) => {
  const location = useLocation();
  const isHomePage = location.pathname === "/";
  const [toggle, setToggle] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { t, i18n } = useTranslation();

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  // Animation controls
  const squareControls = useAnimation();
  const textControls = useAnimation();
  const containerControl = useAnimation();

  // Handle scroll event
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 1) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const startAnimation = async () => {
      try {
        await squareControls.start({
          scale: 1,
          transition: { duration: 0.5, ease: "easeInOut" },
          opacity: 0.5,
        });

        await squareControls.start({
          x: 0,
          scale: 1,
          transition: { duration: 0.9, ease: "easeInOut" },
          opacity: 1,
        });

        await textControls.start({
          x: 0,
          opacity: 1,
          transition: { duration: 1, ease: "easeInOut" },
        });

        await squareControls.start({
          opacity: [1, 0.7, 1],
          transition: { duration: 1, ease: "easeInOut" },
        });

        await textControls.start({
          opacity: [1, 0.7, 1],
          transition: { duration: 1, ease: "easeInOut" },
        });

        await containerControl.start({
          scale: 1,
          transition: { duration: 0.5, ease: "easeInOut" },
        });
      } catch (error) {
        console.error("Animation error:", error);
      }
    };

    startAnimation();
  }, [squareControls, textControls, containerControl]);

  const menuItems = [
    {
      titleKey: 'home',
      link: '/'
    },
    {
      titleKey: 'about',
      link: '/hakkimizda'
    },
    {
      titleKey: 'production',
      link: '/uretim'
    },
    {
      titleKey: 'products',
      link: '/urunler'
    },
    {
      titleKey: 'sustainability',
      link: '/surdurulebilirlik'
    },
    {
      titleKey: 'rnd',
      link: '/ar-ge'
    },
    {
      titleKey: 'socialResponsibility',
      link: '/sosyal-sorumluluk'
    },
    {
      titleKey: 'contact',
      link: '/iletisim'
    }
  ];


  return (
    <>
      <div
        className={`w-full ${isHomePage ? "fixed" : "sticky"
          } top-0 grid grid-cols-10 z-[99] transition-colors duration-300 ${scrolled ? "bg-[rgba(255,255,255,1)]" : "bg-[rgba(255,255,255,0.0)]"
          }`}
      >
        <div
          className={`col-start-2 ${isHomePage
            ? "col-span-8 md:col-start-2 md:col-span-6"
            : "col-span-8"
            }`}
        >
          <div className={`h-24 w-full flex items-center justify-between`}>
            {!isHomePage && <div className={`hidden md:block`}></div>}

            <Link to="/">
              <div className="flex">
                <motion.img
                  animate={squareControls}
                  initial={{ x: "200%", scale: 0.5, opacity: 0 }}
                  src="/images/logo-1-2.svg"
                  className="h-14"
                  alt="Logo"
                />

                <motion.img
                  animate={textControls}
                  initial={{ opacity: 0, x: "0%" }}
                  src="/images/logo-2-2.svg"
                  className="h-14"
                  alt="Logo"
                />
              </div>
            </Link>

            
            <div className={`flex items-center space-x-4 ${isHomePage ? "md:hidden" : "md:flex"
              }`}>
              {/* Language switcher for mobile */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => changeLanguage('tr')}
                  className={`text-12 font-light transition-all ${
                    i18n.language === 'tr' 
                      ? 'text-customBlack font-medium' 
                      : 'text-customGray3'
                  }`}
                >
                  TR
                </button>
                <span className="text-12 text-customGray3">|</span>
                <button
                  onClick={() => changeLanguage('en')}
                  className={`text-12 font-light transition-all ${
                    i18n.language === 'en' 
                      ? 'text-customBlack font-medium' 
                      : 'text-customGray3'
                  }`}
                >
                  EN
                </button>
              </div>
              <HamburgerIcon toggle={toggle} setToggle={setToggle} setHeaderToggle={setHeaderToggle} />
            </div>
          </div>

          <div
            className={`h-14 w-full hidden justify-between items-center ${isHomePage ? "md:flex" : "flex md:hidden"
              }`}
          >
            <div
              className={`text-14 text-customGray3 font-light space-x-24 flex`}
            >
              <div className="flex space-x-8">
                <Link to="hakkimizda">
                  <motion.div
                    animate={{ translateY: 0, opacity: 1 }}
                    initial={{ translateY: -10, opacity: 0 }}
                    transition={{ duration: 1, ease: "easeInOut" }}
                  >
                    {t("header.menu.about")}
                  </motion.div>
                </Link>
                <Link to="urunler">
                  <motion.div
                    animate={{ translateY: 0, opacity: 1 }}
                    initial={{ translateY: -10, opacity: 0 }}
                    transition={{
                      duration: 1,
                      delay: 0.2,
                      ease: "easeInOut",
                    }}
                  >
                    {t("header.menu.products")}
                  </motion.div>
                </Link>
                <Link to="surdurulebilirlik">
                  <motion.div
                    animate={{ translateY: 0, opacity: 1 }}
                    initial={{ translateY: -10, opacity: 0 }}
                    transition={{
                      duration: 1,
                      delay: 0.4,
                      ease: "easeInOut",
                    }}
                  >
                    {t("header.menu.sustainability")}
                  </motion.div>
                </Link>
                <a href="https://www.belgemodul.com/sirket/604" target="_blank">
                  <motion.div
                    animate={{ translateY: 0, opacity: 1 }}
                    initial={{ translateY: -10, opacity: 0 }}
                    transition={{
                      duration: 1,
                      delay: 0.6,
                      ease: "easeInOut",
                    }}
                  >
                    {t("header.menu.informationSociety")}
                  </motion.div>
                </a>
                <Link to="iletisim">
                  <motion.div
                    animate={{ translateY: 0, opacity: 1 }}
                    initial={{ translateY: -10, opacity: 0 }}
                    transition={{
                      duration: 1,
                      delay: 0.8,
                      ease: "easeInOut",
                    }}
                  >
                    {t("header.menu.contact")}
                  </motion.div>
                </Link>
              </div>
              
              <div className="flex items-center space-x-3">
                <motion.button
                  onClick={() => changeLanguage('tr')}
                  animate={{ translateY: 0, opacity: 1 }}
                  initial={{ translateY: -10, opacity: 0 }}
                  transition={{ duration: 1, delay: 0.8, ease: "easeInOut" }}
                  className={`text-14 font-light transition-all ${
                    i18n.language === 'tr' 
                      ? 'text-customBlack font-medium' 
                      : 'text-customGray3 hover:text-customBlack'
                  }`}
                >
                  TR
                </motion.button>
                <motion.div
                  animate={{ translateY: 0, opacity: 1 }}
                  initial={{ translateY: -10, opacity: 0 }}
                  transition={{ duration: 1, delay: 0.8, ease: "easeInOut" }}
                  className="text-14 text-customGray3"
                >
                  |
                </motion.div>
                <motion.button
                  onClick={() => changeLanguage('en')}
                  animate={{ translateY: 0, opacity: 1 }}
                  initial={{ translateY: -10, opacity: 0 }}
                  transition={{ duration: 1, delay: 0.8, ease: "easeInOut" }}
                  className={`text-14 font-light transition-all ${
                    i18n.language === 'en' 
                      ? 'text-customBlack font-medium' 
                      : 'text-customGray3 hover:text-customBlack'
                  }`}
                >
                  EN
                </motion.button>
              </div>
            </div>



            <HamburgerIcon toggle={toggle} setToggle={setToggle} setHeaderToggle={setHeaderToggle} />



          </div>
        </div>
      </div>
      <AnimatePresence>
        {toggle && (
          <motion.div
            animate={{ translateX: toggle ? "0%" : "100%" }}
            initial={{ translateX: "50%" }}
            exit={{ translateX: "50%" }}
            transition={{
              duration: 0.6,
              ease: [0.25, 0.46, 0.45, 0.94],
            }}
            className="fixed w-full h-screen z-[100] top-0 left-0 grid grid-cols-3"
          >
            <div className="col-start-1 md:col-start-2 lg:col-start-3 col-span-3 md:col-span-2 lg:col-span-1 h-full w-full pr-[30%] bg-[rgba(255,255,255,0.95)]">
              <div className="flex flex-col items-center justify-center h-full">
                <div
                  onClick={() => {
                    setToggle(false);
                    setHeaderToggle(false);
                  }}
                  className="h-16 w-full pl-[30%] relative z-[60] cursor-pointer"
                >
                  <div className="relative">
                    <motion.div
                      animate={{ rotate: "45deg" }}
                      initial={{ rotate: "0deg" }}
                      transition={{
                        duration: 1,
                        ease: "easeInOut",
                        delay: 0.3,
                      }}
                      className={"w-10 h-px bg-customGray3 absolute top-8 "}
                    ></motion.div>
                    <motion.div
                      animate={{ rotate: "-45deg" }}
                      initial={{ rotate: "0deg" }}
                      transition={{
                        duration: 1,
                        ease: "easeInOut",
                        delay: 0.3,
                      }}
                      className="w-10 h-px bg-customGray3 absolute top-8 "
                    ></motion.div>
                  </div>
                </div>

                <div className="w-full flex flex-col items-end text-18 font-light text-customGray3 space-y-[10%]">
                  {menuItems.map((item, index) => (
                    <Link to={item.link} key={index}>
                      <motion.div
                        animate={{ opacity: 1, translateX: [10, -20, 0] }}
                        initial={{ opacity: 0 }}
                        transition={{
                          duration: 0.8,
                          delay: index * 0.1,
                          ease: [0.25, 0.46, 0.45, 0.94],
                        }}
                      >
                        {t(`header.menu.${item.titleKey}`)}
                      </motion.div>
                    </Link>
                  ))}
                  <a href="https://www.belgemodul.com/sirket/604" target="_blank">
                    <motion.div
                      animate={{ opacity: 1, translateX: [10, -20, 0] }}
                      initial={{ opacity: 0 }}
                      transition={{
                        duration: 0.8,
                        delay: menuItems.length * 0.1,
                        ease: [0.25, 0.46, 0.45, 0.94],
                      }}
                    >
                      {t("header.menu.informationSociety")}
                    </motion.div>
                  </a>
                  
                  {/* Language switcher in mobile menu */}
                  <motion.div
                    animate={{ opacity: 1, translateX: [10, -20, 0] }}
                    initial={{ opacity: 0 }}
                    transition={{
                      duration: 0.8,
                      delay: menuItems.length * 0.1,
                      ease: [0.25, 0.46, 0.45, 0.94],
                    }}
                    className="flex items-center space-x-3 pt-8"
                  >
                    <button
                      onClick={() => changeLanguage('tr')}
                      className={`text-16 font-light transition-all ${
                        i18n.language === 'tr' 
                          ? 'text-customBlack font-medium' 
                          : 'text-customGray3'
                      }`}
                    >
                      TR
                    </button>
                    <span className="text-16 text-customGray3">|</span>
                    <button
                      onClick={() => changeLanguage('en')}
                      className={`text-16 font-light transition-all ${
                        i18n.language === 'en' 
                          ? 'text-customBlack font-medium' 
                          : 'text-customGray3'
                      }`}
                    >
                      EN
                    </button>
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
